import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { IconRefresh } from '@tabler/icons-react';

import useAppStore from '@/shared/store/app.store';

import classes from './PullToRefresh.module.css';

const PULL_THRESHOLD = 70;
const MAX_PULL = 110;
const DAMPING = 0.5;
const INDICATOR_SIZE = 36;
const INDICATOR_TOP_OFFSET = 8;
const HIDDEN_OFFSET = -(INDICATOR_SIZE + INDICATOR_TOP_OFFSET);

const isIOSStandaloneRuntime = () => {
	if (typeof window === 'undefined') return false;

	const isStandalone =
		window.matchMedia('(display-mode: standalone)').matches ||
		(window.navigator as { standalone?: boolean }).standalone === true;

	const isIOS =
		/iPad|iPhone|iPod/.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

	return isStandalone && isIOS;
};

export const PullToRefresh = () => {
	const isMobile = useAppStore((state) => state.isMobile);
	const [iosStandalone] = useState(isIOSStandaloneRuntime);
	const enabled = iosStandalone && !!isMobile;

	const [pullDistance, setPullDistance] = useState(0);
	const [isPulling, setIsPulling] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const startYRef = useRef<number | null>(null);
	const activeRef = useRef(false);
	const distanceRef = useRef(0);
	const isRefreshingRef = useRef(false);

	useEffect(() => {
		isRefreshingRef.current = isRefreshing;
	}, [isRefreshing]);

	useEffect(() => {
		if (!enabled) return;

		const container = document.getElementById('root');
		if (!container) return;

		const getScrollTop = () =>
			container.scrollTop || window.scrollY || document.documentElement.scrollTop || 0;

		const onTouchStart = (e: TouchEvent) => {
			if (isRefreshingRef.current) return;
			if (e.touches.length !== 1) return;
			if (getScrollTop() > 0) {
				activeRef.current = false;
				return;
			}
			startYRef.current = e.touches[0].clientY;
			activeRef.current = true;
		};

		const onTouchMove = (e: TouchEvent) => {
			if (!activeRef.current || startYRef.current === null) return;

			const delta = e.touches[0].clientY - startYRef.current;

			if (delta <= 0) {
				if (distanceRef.current !== 0) {
					distanceRef.current = 0;
					setPullDistance(0);
					setIsPulling(false);
				}
				return;
			}

			const damped = Math.min(MAX_PULL, delta * DAMPING);
			distanceRef.current = damped;
			setPullDistance(damped);
			setIsPulling(true);

			if (delta > 5 && e.cancelable) e.preventDefault();
		};

		const onTouchEnd = () => {
			const wasActive = activeRef.current;
			activeRef.current = false;
			startYRef.current = null;
			if (!wasActive) return;

			setIsPulling(false);

			if (distanceRef.current >= PULL_THRESHOLD) {
				setIsRefreshing(true);
				setPullDistance(PULL_THRESHOLD);
				window.setTimeout(() => window.location.reload(), 400);
			} else {
				distanceRef.current = 0;
				setPullDistance(0);
			}
		};

		container.addEventListener('touchstart', onTouchStart, { passive: true });
		container.addEventListener('touchmove', onTouchMove, { passive: false });
		container.addEventListener('touchend', onTouchEnd, { passive: true });
		container.addEventListener('touchcancel', onTouchEnd, { passive: true });

		return () => {
			container.removeEventListener('touchstart', onTouchStart);
			container.removeEventListener('touchmove', onTouchMove);
			container.removeEventListener('touchend', onTouchEnd);
			container.removeEventListener('touchcancel', onTouchEnd);
		};
	}, [enabled]);

	if (!enabled) return null;

	const visible = pullDistance > 0 || isRefreshing;
	const progress = Math.min(1, pullDistance / PULL_THRESHOLD);
	const translateY = visible ? pullDistance : HIDDEN_OFFSET;
	const opacity = visible ? Math.min(1, progress + 0.3) : 0;
	const rotation = isRefreshing ? 0 : progress * 270;

	return (
		<div className={classes.container} aria-hidden>
			<div
				className={classNames(classes.indicator, { [classes.indicatorIdle]: !isPulling })}
				style={{
					transform: `translateY(${translateY}px)`,
					opacity,
				}}
			>
				<span
					className={classNames(classes.icon, { [classes.spin]: isRefreshing })}
					style={isRefreshing ? undefined : { transform: `rotate(${rotation}deg)` }}
				>
					<IconRefresh size={18} />
				</span>
			</div>
		</div>
	);
};
